/**
 * The column universe — every column a user could show, from every source.
 *
 * Deliberately a **merge**, not a passthrough of the server's metadata. Today
 * every column comes from qv-data, but `docs/plans/screener-dsl.md` adds
 * user-authored computed columns that exist only in `ScreenerConfig` and will
 * never appear in the server's `columns` frame. Sourcing the picker from the
 * merge now means those appear alongside server columns the day they land,
 * instead of the picker having to be rebuilt around them.
 *
 * Precedence, most to least specific:
 *   1. the contract's per-column overrides (`header`, `type`)
 *   2. the server's metadata (`label`, `description`, `timeframe`)
 *   3. the Arrow schema (inferred type)
 *
 * A column is *renderable* only if it has data behind it — a server field or a
 * local definition. One that has neither (a saved column whose field the
 * upstream engine dropped) is excluded rather than offered as a dead entry.
 *
 * ## The column serving coordinator
 *
 * This is also where the screener decides, per column, **where its value comes
 * from and whether it is here yet** — `docs/screener-compute-design.html` §6
 * names this module as that coordinator, which it already was: the merge of the
 * Arrow schema, the server's metadata and the contract's config is exactly the
 * knowledge the question needs. What changed is the vocabulary, from a
 * two-value `server`/`computed` to three origins and a state, because a browser
 * `valueGetter` and a compute service are not the same place and "no value" is
 * no longer one thing.
 */

import { CONDITION_FIELD_PREFIX } from "./filter-eval.js";
import type { ScreenerColumn } from "@qufolio/model";
import { canCompute, canRenderImage, type RenderType } from "./columns.js";
import { needsHistory } from "./expr.js";

/**
 * The company logo, as one column definition every screen can offer.
 *
 * Its `field` is only an id — the content comes from the template, which is
 * what lets it sit beside the `symbol` column reading the same value. FMP's
 * image host needs no key and reads nothing but the symbol.
 *
 * **Offered on every screen with a `symbol`, saved or not** (owner,
 * 2026-10-06). The picker lists only configured columns and server fields, and
 * the server sends no `logo`, so a screen saved before `DEFAULT_COLUMNS` carried
 * one had no way to get it.
 */
export const LOGO_COLUMN: ScreenerColumn = {
  field: "logo",
  header: "",
  type: "image",
  imageUrl: "https://financialmodelingprep.com/image-stock/{symbol}.png",
  pinned: "left",
  width: 32,
};
import {
  formatTimeframe,
  type ScreenerColumnMeta,
  type ScreenerField,
  type ScreenerTimeframe,
} from "@qufolio/transport";

/**
 * Where a column's value comes from, and therefore which wave it arrives in.
 *
 * | | served by | wave |
 * |---|---|---|
 * | `stored` | `qf-us` — the bars and the `symbol` table | 1 |
 * | `client` | the browser, from the row it already has | 1 |
 * | `compute` | whoever computes expressions over bar history | 2 |
 *
 * `needsHistory` is the whole test between the last two, and it is the *same*
 * test `serverComputedColumns` builds the request from — one rule, so the
 * request and the rendering cannot disagree about which wave a column is in.
 */
export type ColumnOrigin = "stored" | "client" | "compute";

/**
 * Whether this column has a value, is waiting for its first, or is waiting for
 * nobody.
 *
 * **`pending` is a state a column leaves once and never re-enters.** It means
 * *no value has ever arrived*, not *a fresh value is being computed* — the two
 * waves run on their own clocks, so between pushes the previous value stays on
 * screen and is swapped when the next one lands. Reverting to a spinner every
 * cycle would make a computed column unreadable, and the price columns beside it
 * already behave this way. See `column-wave.ts` for the merge that guarantees
 * it.
 *
 * **`unavailable` is not `pending`, and confusing them is the failure this
 * exists to prevent.** A spinner over a column nobody is computing never
 * resolves, and it looks identical to a compute service that is merely slow.
 */
export type ColumnState = "ready" | "pending" | "unavailable";

/** One entry in the picker: a column, everything known about it, and its state. */
export interface UniverseColumn {
  field: string;
  /** Header text: contract override, else server label, else the field name. */
  header: string;
  /** Friendly searchable name; empty when the dictionary hasn't landed. */
  description: string;
  type: RenderType;
  timeframe?: ScreenerTimeframe;
  /**
   * The id minus its timeframe prefix — the daily, weekly and monthly copies of
   * one measure share it, which is how the picker draws them as one row with
   * D / W / M chips. The field itself when the server says nothing.
   */
  baseField: string;
  /** The server's category (`Price`, `Valuation`, `Momentum`); the picker's topics. */
  category?: string;
  /** A computed column the user wrote — it lives only in the config. */
  authored?: true;
  origin: ColumnOrigin;
  /** Whether a value is here, coming, or never coming. */
  state: ColumnState;
  /** Currently shown in the grid. */
  visible: boolean;
  /** Position among visible columns; `Infinity` when hidden. */
  order: number;
}

export interface BuildUniverseOptions {
  /** Fields discovered in the Arrow schema of the frame being rendered. */
  fields: readonly ScreenerField[];
  /** Per-column metadata from qv-data's `columns` frame. May be empty. */
  meta?: readonly ScreenerColumnMeta[];
  /** The contract's column view. */
  columns?: readonly ScreenerColumn[];
  /**
   * What the server acknowledged serving, per column — `readColumnSources` on
   * the same frames. **Absence is the answer, never a missing input:** a
   * computed column the server did not acknowledge has nobody computing it, so
   * an omitted map (a server predating the field, which is every server in
   * phase 1) correctly reports every history-needing column as `unavailable`.
   */
  sources?: ReadonlyMap<string, ColumnOrigin>;
}

/**
 * Whether a column has a value, given where it comes from and what the server
 * said about it.
 *
 * **A value on screen outranks the acknowledgement**, which is the ordering that
 * makes this shippable before the server sends anything: today's payload
 * delivers every column in wave 1, so a history-needing computed column arrives
 * as a real Arrow field, `hasValue` is true, and it renders exactly as it always
 * did. It is also what makes `pending` one-way — the Arrow schema is the
 * memory, because the wave merge is additive and never removes a field, so
 * "the field exists" is precisely "a value arrived at some point".
 */
function columnState(
  origin: ColumnOrigin,
  hasValue: boolean,
  acknowledged: ColumnOrigin | undefined,
): ColumnState {
  if (hasValue) return "ready";
  // Only a compute column can be waiting for someone. Anything else with no
  // value has no source at all — and a `stored` one never reaches here, since
  // it is excluded from the universe above for the picker's sake.
  if (origin !== "compute") return "unavailable";
  return acknowledged === "compute" ? "pending" : "unavailable";
}

/**
 * Build the full universe: configured columns in their configured order, then
 * every remaining renderable field, hidden.
 *
 * Mirrors `buildColumnDefs` so the picker and the grid can never disagree about
 * which columns exist or what order they're in.
 */
export function buildUniverse({
  fields: allFields,
  meta: allMeta = [],
  columns = [],
  sources,
}: BuildUniverseOptions): UniverseColumn[] {
  // A ScanScript condition's hidden field arrives like a computed column's,
  // and is never one: it exists only for the filter to read.
  // A script-only field (the patterns' 0/1/2 numbers) likewise: ScanScript
  // reads it, the picker never offers it — `pattern` is the one column.
  const scriptOnly = new Set(allMeta.filter((m) => m.scriptOnly).map((m) => m.field));
  const offered = (field: string) =>
    !field.startsWith(CONDITION_FIELD_PREFIX) && !scriptOnly.has(field);
  const fields = allFields.filter((f) => offered(f.name));
  const meta = allMeta.filter((m) => offered(m.field));
  const typeByField = new Map(fields.map((f) => [f.name, f.type]));
  const metaByField = new Map(meta.map((m) => [m.field, m]));
  const hasView = columns.length > 0;

  const entry = (
    field: string,
    column: ScreenerColumn | undefined,
    visible: boolean,
    order: number,
  ): UniverseColumn | undefined => {
    const columnMeta = metaByField.get(field);
    const inferred = typeByField.get(field);

    // A computed column has no Arrow field of its own — it is defined locally,
    // which is exactly why this function merges rather than passing the server's
    // metadata through. What it needs instead is its *inputs*: without them the
    // expression could only ever yield nulls, so it is no more renderable than a
    // dropped upstream column.
    const computed = column?.computed !== undefined;
    const image = column?.type === "image";
    if (computed) {
      if (!canCompute(column!, typeByField)) return undefined;
    } else if (image) {
      // An image column's `field` is only an id; its template supplies content.
      if (!canRenderImage(column!, typeByField)) return undefined;
    } else if (inferred === undefined && sources?.get(field) !== "compute") {
      // No Arrow field and no local definition means nothing would render.
      //
      // **Still excluded, and still right for the picker** — a saved column
      // whose upstream field is gone is a dead entry, not a slow one. The state
      // below is for the other case: a computed column whose value has a place
      // to come from, or provably has not.
      return undefined;
    }

    // A stored column a server said is coming (`source: compute`, the Impulse
    // columns in session) waits like a computed one: pending until it lands.
    const origin: ColumnOrigin = computed
      ? needsHistory(column!.computed!.expression)
        ? "compute"
        : "client"
      : inferred === undefined && sources?.get(field) === "compute"
        ? "compute"
        : "stored";
    // A client column is evaluated from the row that is already here, and an
    // image column's content comes from its template — both have their value the
    // moment the row does. Everything else is answered by the Arrow schema.
    const hasValue = origin === "client" || image || inferred !== undefined;

    return {
      field,
      header: column?.header || columnMeta?.label || field,
      description: computed
        ? (column!.computed!.source ?? "")
        : (columnMeta?.description ?? ""),
      // Computed columns default to number: the scalar catalog is arithmetic.
      type: column?.type ?? inferred ?? "number",
      ...(columnMeta?.timeframe ? { timeframe: columnMeta.timeframe } : {}),
      baseField: columnMeta?.baseField || field,
      ...(!computed && columnMeta?.category ? { category: columnMeta.category } : {}),
      ...(computed ? { authored: true as const } : {}),
      origin,
      state: columnState(origin, hasValue, sources?.get(field)),
      visible,
      order,
    };
  };

  const out: UniverseColumn[] = [];
  const placed = new Set<string>();
  let order = 0;

  for (const column of columns) {
    const visible = column.hide !== true;
    // `order` counts visible columns only, so a hidden one gets no position —
    // it has no place among what's on screen until it is shown again.
    const built = entry(column.field, column, visible, visible ? order : Infinity);
    if (!built) continue; // upstream dropped or renamed it
    placed.add(column.field);
    if (visible) order += 1;
    out.push(built);
  }

  // The logo, hidden, when the screen has no image column of its own. Named
  // "Logo" here only: the grid header stays blank, as it is on a new screen.
  if (!placed.has(LOGO_COLUMN.field) && !columns.some((c) => c.type === "image")) {
    const logo = entry(LOGO_COLUMN.field, LOGO_COLUMN, false, Infinity);
    if (logo) {
      placed.add(LOGO_COLUMN.field);
      out.push({ ...logo, header: "Logo" });
    }
  }

  for (const field of fields) {
    if (placed.has(field.name)) continue;
    // With no view at all, everything shows — the right default for a screen
    // nobody has customised. Otherwise unlisted fields stay available but hidden.
    const built = entry(field.name, undefined, !hasView, hasView ? Infinity : order);
    if (!built) continue;
    if (built.visible) order += 1;
    out.push(built);
  }

  return out;
}

/** Group the universe by timeframe, screen-native columns first. */
export interface TimeframeGroup {
  /** `""` for the screen's own timeframe, else the canonical `[count]unit`. */
  key: string;
  label: string;
  columns: UniverseColumn[];
}

/** Singular noun, and the adverb for a count of 1 — English is irregular here
 * ("Daily", not "Dayly"), so both are spelled out rather than derived. */
const UNIT_NAMES: Record<ScreenerTimeframe["unit"], { noun: string; every: string }> = {
  s: { noun: "Second", every: "Per second" },
  mi: { noun: "Minute", every: "Per minute" },
  h: { noun: "Hour", every: "Hourly" },
  d: { noun: "Day", every: "Daily" },
  w: { noun: "Week", every: "Weekly" },
  m: { noun: "Month", every: "Monthly" },
  q: { noun: "Quarter", every: "Quarterly" },
  y: { noun: "Year", every: "Yearly" },
};

/** Human name for a timeframe: `w` -> "Weekly", `2w` -> "2 Week", `15mi` -> "15 Minute". */
export function timeframeLabel(timeframe: ScreenerTimeframe | undefined): string {
  if (!timeframe) return "Screen timeframe";
  const unit = UNIT_NAMES[timeframe.unit];
  return timeframe.count === 1 ? unit.every : `${timeframe.count} ${unit.noun}`;
}

/** Short badge text for a header: the canonical prefix, upper-cased. */
export function timeframeBadge(timeframe: ScreenerTimeframe | undefined): string {
  return timeframe ? formatTimeframe(timeframe).toUpperCase() : "";
}

export function groupByTimeframe(columns: readonly UniverseColumn[]): TimeframeGroup[] {
  const groups = new Map<string, TimeframeGroup>();

  for (const column of columns) {
    const key = column.timeframe ? formatTimeframe(column.timeframe) : "";
    let group = groups.get(key);
    if (!group) {
      group = { key, label: timeframeLabel(column.timeframe), columns: [] };
      groups.set(key, group);
    }
    group.columns.push(column);
  }

  // Screen-native first; the rest alphabetically by key so ordering is stable
  // across refreshes rather than following column order.
  return [...groups.values()].sort((a, b) => {
    if (a.key === b.key) return 0;
    if (a.key === "") return -1;
    if (b.key === "") return 1;
    return a.key.localeCompare(b.key);
  });
}

/**
 * Filter the universe by a free-text query.
 *
 * Matches field, header and description, so a user can find a column by the
 * short name on screen *or* by its friendly name — which is the point of
 * carrying both. Blank query returns everything.
 */
export function searchColumns(
  columns: readonly UniverseColumn[],
  query: string,
): UniverseColumn[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...columns];
  return columns.filter(
    (c) =>
      c.field.toLowerCase().includes(needle) ||
      c.header.toLowerCase().includes(needle) ||
      c.description.toLowerCase().includes(needle),
  );
}

/**
 * Apply a visibility toggle to the contract's column view.
 *
 * Returns the full ordered list — visible columns keep their order, and a column
 * being hidden stays in the list so its position and overrides survive being
 * switched off and on again.
 */
export function toggleColumn(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
  field: string,
  visible: boolean,
): ScreenerColumn[] {
  return rebuild(universe, columns, (entry, shown) =>
    entry.field === field ? visible : shown,
  );
}

/**
 * Show several columns at once, leaving every other one as it was.
 *
 * **Not `toggleColumn` in a loop**, and the reason is the shape of the function
 * above: it rebuilds the whole list from the *universe*, whose `visible` flags
 * are a snapshot of the call it was given. Folding it per field therefore
 * re-hides everything the previous iteration turned on — the second call reads
 * the same stale universe and writes `hide` back onto the first's work.
 *
 * One pass, one decision per column, sharing `rebuild` with the single toggle so
 * there is one answer to what showing a column means: an unconfigured field
 * gains an entry, a hidden one loses its `hide`, and a shown one keeps its place
 * rather than moving to the end.
 */
export function showColumns(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
  fields: readonly string[],
): ScreenerColumn[] {
  const wanted = new Set(fields);
  return rebuild(universe, columns, (entry, shown) => wanted.has(entry.field) || shown);
}

/**
 * Whether the Columns tab offers "+ Logo" (owner, 2026-10-06).
 *
 * Read off the universe rather than re-derived: `buildUniverse` offers the logo
 * only when the screen has no image column *and* `canRenderImage` passes on this
 * frame — i.e. there is a `symbol` to interpolate. So "the logo is in the
 * universe but not yet in the config" is exactly "adding it would draw
 * something". The image check is repeated because the button is a promise that
 * a column will appear, and a screen with its own image column already has one.
 */
export function canAddLogo(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
): boolean {
  if (columns.some((c) => c.type === "image" || c.field === LOGO_COLUMN.field)) return false;
  return universe.some((u) => u.field === LOGO_COLUMN.field && u.type === "image");
}

/**
 * Add the logo: shown, pinned left, directly in front of Symbol.
 *
 * **Through `toggleColumn`, not a hand-built entry**, so `rebuild` writes the one
 * `LOGO_COLUMN` definition and there is no second copy to drift.
 *
 * **Then moved in front of `symbol`, because pinning alone does not place it.**
 * AG Grid draws the pinned-left block in definition order, and `buildColumnDefs`
 * emits definitions in config order — so the logo `rebuild` leaves after the
 * configured columns would draw to the *right* of a pinned Symbol, the reverse
 * of what a new screen shows (`DEFAULT_COLUMNS` is logo, then Symbol). With no
 * `symbol` entry it stays where
 * `rebuild` put it; pinned left, it still draws in the pinned block.
 */
export function addLogo(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
): ScreenerColumn[] {
  const next = toggleColumn(universe, columns, LOGO_COLUMN.field, true);
  const from = next.findIndex((c) => c.field === LOGO_COLUMN.field);
  const to = next.findIndex((c) => c.field === "symbol");
  if (from < 0 || to < 0 || from < to) return next;
  const [logo] = next.splice(from, 1);
  next.splice(to, 0, logo!);
  return next;
}

/**
 * The whole column list, rebuilt with `decide` answering for each column.
 *
 * **What a column shows *now* is read from `columns`, not from the universe.**
 * The two agree whenever the universe was built from these columns — which is
 * the ordinary case, since `buildUniverse` derives `visible` from `hide !== true`
 * — and they part company the moment a caller applies two changes without
 * rebuilding in between. The universe is then a snapshot of the state before the
 * first change, so deciding from it silently reverts that change. `columns` is
 * the one that was just written.
 *
 * The universe still answers for a field with no entry, which is the case it
 * knows and `columns` does not.
 */
function rebuild(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
  decide: (entry: UniverseColumn, shownNow: boolean) => boolean,
): ScreenerColumn[] {
  const configured = new Map(columns.map((c) => [c.field, c]));
  return universe.map((entry) => {
    const existing = configured.get(entry.field);
    // The offered logo has no stored column yet; turning it on writes the whole
    // definition, or the grid would get a `logo` field with no template.
    const base: ScreenerColumn = existing
      ? { ...existing }
      : entry.field === LOGO_COLUMN.field && entry.type === "image"
        ? { ...LOGO_COLUMN }
        : { field: entry.field };
    const shownNow = existing ? existing.hide !== true : entry.visible;
    if (decide(entry, shownNow)) delete base.hide;
    else base.hide = true;
    return base;
  });
}

/**
 * Metadata for the columns a user authored, so they are as usable as any other.
 *
 * These live only in `ScreenerConfig` and are in no server frame, which made
 * them invisible three ways at once: unpickable in `<FieldRef>`, unfilterable,
 * and — least obviously — absent from the field list sent to the compiler, so
 * typing their name failed with "unknown column". One list feeds all three, so
 * one merge fixes all three.
 *
 * `rawField` is empty because there is no upstream name to trace back to, and
 * the description is the column's own DSL: it says what the column is better
 * than any label could, and it is what the author wrote.
 */
export function computedMeta(
  columns: readonly ScreenerColumn[] | undefined,
): ScreenerColumnMeta[] {
  return (columns ?? [])
    .filter((c) => c.computed)
    .map((c) => ({
      field: c.field,
      rawField: "",
      baseField: c.field,
      label: c.header ?? "",
      description: c.computed!.source ?? "Computed column.",
    }));
}

/**
 * Server metadata plus the user's own columns.
 *
 * `exclude` drops one field — pass the column being edited, so its own
 * expression cannot reference it. A self-reference compiles happily and then
 * recurses, and the editor is the only place that knows which column that is.
 */
export function mergedMeta(
  meta: readonly ScreenerColumnMeta[],
  columns: readonly ScreenerColumn[] | undefined,
  exclude?: string,
): ScreenerColumnMeta[] {
  const known = new Set(meta.map((m) => m.field));
  // A user's `header` overrides the server's label, here rather than in each
  // picker. Otherwise a renamed column keeps displaying — and searching by —
  // the name the server gave it, and the name its owner chose finds nothing.
  const headers = new Map(
    (columns ?? []).filter((c) => c.header).map((c) => [c.field, c.header!]),
  );
  const withHeaders = meta.map((m) =>
    headers.has(m.field) ? { ...m, label: headers.get(m.field)! } : m,
  );
  return [...withHeaders, ...computedMeta(columns).filter((m) => !known.has(m.field))].filter(
    (m) => m.field !== exclude && !m.field.startsWith(CONDITION_FIELD_PREFIX),
  );
}

/**
 * Move one configured column to sit where another one sits.
 *
 * **Addressed by field, and applied to `columns` rather than to the universe.**
 * The universe is configured columns in their configured order *followed by*
 * every column the server offers, so a position in it is only meaningful for the
 * first part — and `columns` is the array that decides that part. Moving inside
 * the list the user can see therefore means moving inside `columns`.
 *
 * Hidden entries come along untouched. `columns` holds both shown and hidden
 * columns while `order` counts only the shown ones, so a move expressed as
 * "index 3 to index 1" would land somewhere else entirely the moment a hidden
 * column sat between them. Re-inserting relative to the *target field* is
 * immune to that: it is the same answer whatever is hidden.
 *
 * A field that is not configured, or a move onto itself, returns the input
 * unchanged — by identity, so a drop on the row you picked up costs no save.
 */
export function moveColumn(
  columns: readonly ScreenerColumn[],
  fromField: string,
  toField: string,
): readonly ScreenerColumn[] {
  if (fromField === toField) return columns;
  const from = columns.findIndex((c) => c.field === fromField);
  const to = columns.findIndex((c) => c.field === toField);
  if (from < 0 || to < 0) return columns;

  const next = [...columns];
  const [moved] = next.splice(from, 1);
  // After the removal every index past `from` has shifted down by one, so the
  // target's new position is what decides where this lands — computing it
  // before the splice is the off-by-one every reorder gets wrong once.
  next.splice(next.findIndex((c) => c.field === toField) + (from < to ? 1 : 0), 0, moved!);
  return next;
}
